// Поиск и команды ⌘K (задача D5, ui-spec §2): Ctrl K или ⌘K открывают и закрывают, без анимации (ui-spec §5).
//
// - Поле «Раздел, номер тикета, ссылка на чат или команда».
// - «Перейти» — разделы, открытые роли.
// - «Диалог» — когда введён номер: «Открыть диалог #48213» (`GET /api/v1/dialogs/lookup`, D8) и «Почему не ответила?
//   #48213» → `/readiness?why=48213`; ссылка на чат Омнидеска или его id (план PH) — «Открыть диалог по ссылке Омнидеска».
//   Диалога нет — тост с текстом сервера.
// - «Действия» — у админа «Проверить всё».
// - «Тема» — Система, светлая, тёмная.
// - Ничего не подошло — «Ничего не нашлось»; при номере тикета не показывается: пункты «Диалог» есть всегда.
// - «Проверить всё» — тост «Проверяем…», который сменяется итогом или ошибкой с «Повторить» (при обрыве связи — без
//   него: «Повторить» уже в баннере «Нет связи»).
// - Внизу — подсказка клавиш (так у Devin и Linear).
import type { Role } from '@contract/index.js';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CircleHelp, ListChecks, MessagesSquare, Monitor, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { messageRuOf } from '@/api/errors';
import { lookupDialog, ticketRefOf, type TicketRef } from '@/api/queries/dialogs';
import { runChecks } from '@/api/queries/readiness';
import { can } from '@/auth/permissions';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { formatTime } from '@/lib/format';
import { setThemePref, THEME_RU, type ThemePref } from '@/lib/theme';
import { navItems } from './nav';
import { toastActionError, toastError } from './toaster';

export const PALETTE_PLACEHOLDER_RU = 'Раздел, номер тикета, ссылка на чат или команда';

const THEME_ICONS = { system: Monitor, light: Sun, dark: Moon } as const;

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly role: Role;
}

export function CommandPalette({ open, onOpenChange, role }: CommandPaletteProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const ref = ticketRefOf(search);
  const ticket = ref?.kind === 'number' ? String(ref.number) : null;

  function close(): void {
    onOpenChange(false);
    setSearch('');
  }

  function go(href: string): void {
    close();
    void navigate(href);
  }

  function openDialog(target: TicketRef): void {
    close();
    void lookupDialog(target).then(
      (dialogId) => navigate(`/dialogs/${encodeURIComponent(dialogId)}`),
      (error: unknown) => {
        toastError(messageRuOf(error));
      },
    );
  }

  function checkAll(): void {
    close();
    const id = toast.loading('Проверяем всё…');
    void runChecks(queryClient).then(
      (snapshot) => {
        toast.success(`Проверено · ${formatTime(snapshot.checkedAt)} МСК`, { id });
      },
      (error: unknown) => {
        toastActionError('Не удалось проверить', error, checkAll, { id });
      },
    );
  }

  function setTheme(pref: ThemePref): void {
    close();
    setThemePref(pref);
  }

  return (
    <CommandDialog
      title="Поиск и команды"
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearch('');
        onOpenChange(next);
      }}
    >
      {/* Пункт «Диалог» сам ищет по номеру: свой фильтр cmdk для него не нужен — `forceMount`. */}
      <CommandInput placeholder={PALETTE_PLACEHOLDER_RU} value={search} onValueChange={setSearch} aria-label={PALETTE_PLACEHOLDER_RU} />
      <CommandList>
        {ref === null && <CommandEmpty>Ничего не нашлось</CommandEmpty>}
        {ref?.kind === 'omni' && (
          <CommandGroup heading="Диалог" forceMount>
            <CommandItem
              forceMount
              value={`dialog-${ref.id}`}
              onSelect={() => {
                openDialog(ref);
              }}
            >
              <MessagesSquare aria-hidden="true" />
              Открыть диалог по ссылке Омнидеска
            </CommandItem>
          </CommandGroup>
        )}
        {ref?.kind === 'number' && ticket !== null && (
          <CommandGroup heading="Диалог" forceMount>
            <CommandItem
              forceMount
              value={`dialog-${ticket}`}
              onSelect={() => {
                openDialog(ref);
              }}
            >
              <MessagesSquare aria-hidden="true" />
              {`Открыть диалог #${ticket}`}
            </CommandItem>
            <CommandItem
              forceMount
              value={`why-${ticket}`}
              onSelect={() => {
                go(`/readiness?why=${ticket}`);
              }}
            >
              <CircleHelp aria-hidden="true" />
              {`Почему не ответила? #${ticket}`}
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup heading="Перейти">
          {navItems(role).map((item) => (
            <CommandItem
              key={item.id}
              value={item.titleRu}
              onSelect={() => {
                go(item.href);
              }}
            >
              <item.icon aria-hidden="true" />
              {item.titleRu}
              <CommandShortcut>
                <ArrowRight aria-hidden="true" className="size-3.5" />
              </CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>
        {can(role, 'run_checks') && (
          <CommandGroup heading="Действия">
            <CommandItem value="Проверить всё" keywords={['готовность', 'проверки']} onSelect={checkAll}>
              <ListChecks aria-hidden="true" />
              Проверить всё
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup heading="Тема">
          {(['system', 'light', 'dark'] as const).map((pref) => {
            const Icon = THEME_ICONS[pref];
            return (
              <CommandItem
                key={pref}
                value={`Тема: ${THEME_RU[pref]}`}
                keywords={['тема', 'оформление']}
                onSelect={() => {
                  setTheme(pref);
                }}
              >
                <Icon aria-hidden="true" />
                {`Тема: ${THEME_RU[pref].toLowerCase()}`}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
      <div aria-hidden="true" className="flex shrink-0 items-center gap-3 border-t border-border px-3 py-2 text-sm text-muted-foreground max-sm:hidden">
        <span>↑↓ — выбрать</span>
        <span>Enter — открыть</span>
        <span className="ml-auto">Esc — закрыть</span>
      </div>
    </CommandDialog>
  );
}
